import React, { useState, useRef, useEffect } from 'react';
import { builderChatSend, getBuilderChat, saveBuilderChatStrategy } from '../api';
import GraphDiffCard from './GraphDiffCard';
import BacktestSummaryCard from './BacktestSummaryCard';
import VersionTimeline from './VersionTimeline';
import Spinner from './Spinner';
import ExportStrategyModal from './ExportStrategyModal';
import { track, Events } from '../analytics';

const MAX_TEXTAREA_HEIGHT = 200;

/**
 * Self-contained conversational AI strategy builder panel. Sends turns to
 * POST /builder-chat/ and hands any resulting graph back up via
 * onGraphUpdate — it never touches StrategyBuilder's canvas state directly.
 */
export default function BuilderChatPanel({ onGraphUpdate, isMobile, onClose, onRunBacktest, onViewResults }) {
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [versions, setVersions] = useState([]);
  const [currentStrategyId, setCurrentStrategyId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState('');
  // Name-prompt modal for the per-message "Save strategy" chip: { defaultName }
  const [saveModal, setSaveModal] = useState(null);
  // Export-a-message's-graph modal: { graph, name }
  const [exportTarget, setExportTarget] = useState(null);
  // Post-save "run it now?" prompt: { id, name }
  const [runPrompt, setRunPrompt] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-grow the composer as the user types (capped at MAX_TEXTAREA_HEIGHT),
  // and give it a taller starting point for the very first message, since
  // those tend to be multi-paragraph strategy descriptions rather than
  // one-line follow-ups like "run it" or "yes".
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT) + 'px';
  }, [input]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const refreshVersions = async (effectiveChatId) => {
    if (!effectiveChatId) return;
    try {
      const detail = await getBuilderChat(effectiveChatId);
      setVersions(detail.data.version_chain || []);
      setCurrentStrategyId(detail.data.current_strategy?.id || null);
    } catch { /* non-blocking */ }
  };

  const send = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    setSending(true);
    setSaveNote('');
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    try {
      const resp = await builderChatSend({ chat_id: chatId, message: trimmed });
      const data = resp.data;
      const effectiveChatId = data.chat_id || chatId;
      if (!chatId) setChatId(effectiveChatId);

      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: data.reply,
        diff: data.diff,
        backtest: data.backtest,
        suggestions: data.suggestions,
        name: data.name,
        createdStrategy: !!data.graph && !data.no_change,
        graph: data.graph || null,
        fromCode: !!data.from_code,
      }]);

      if (data.graph && onGraphUpdate) {
        onGraphUpdate(data.graph, data.name, data.diff);
      }
      if (data.graph || data.intent === 'compare_versions') {
        refreshVersions(effectiveChatId);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Something went wrong.';
      setMessages((prev) => [...prev, { role: 'assistant', content: detail, isError: true }]);
    } finally {
      setSending(false);
    }
  };

  const handleVersionSelect = (version) => {
    if (!version || version.strategy_id === currentStrategyId) return;
    // Reuse the exact stored change summary so the backend's keyword match
    // (strategies/builder_chat.py:resolve_target_version) reliably resolves
    // to this specific version rather than falling through to the LLM.
    send(`Switch to version ${version.index}: ${version.change_summary}`);
  };

  const finishSave = (strategyData) => {
    setSaveNote(`Saved as "${strategyData.name}" — visible in your strategy list.`);
    refreshVersions(chatId);
    if (onRunBacktest) setRunPrompt({ id: strategyData.id, name: strategyData.name });
  };

  const handleSave = async () => {
    if (!chatId || saving) return;
    setSaving(true);
    setSaveNote('');
    try {
      const resp = await saveBuilderChatStrategy(chatId);
      finishSave(resp.data);
    } catch (err) {
      setSaveNote(err.response?.data?.detail || err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // Chip-triggered save: prompts for a name first via saveModal.
  const openSaveModal = () => {
    const latestName = [...messages].reverse().find((m) => m.name)?.name || '';
    setSaveModal({ defaultName: latestName });
  };

  const confirmSaveWithName = async (name) => {
    if (!chatId || saving) return;
    setSaving(true);
    setSaveNote('');
    try {
      const resp = await saveBuilderChatStrategy(chatId, name.trim() ? { name: name.trim() } : {});
      setSaveModal(null);
      finishSave(resp.data);
    } catch (err) {
      setSaveNote(err.response?.data?.detail || err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = () => {
    if (runPrompt && onRunBacktest) onRunBacktest({ id: runPrompt.id });
    setRunPrompt(null);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--card)',
      width: isMobile ? '100%' : 380,
      flexShrink: 0,
      // On mobile this panel replaces the canvas as a column-flex child and
      // needs flex:1 to fill the remaining height; on desktop it's a row-flex
      // sibling where a fixed width (not flex-grow) is what we want.
      ...(isMobile ? { flex: 1, minHeight: 0 } : { borderLeft: '1px solid var(--border)' }),
    }}>
      <div style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>💬 AI Builder Chat</div>
        {currentStrategyId && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '5px 12px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 12,
              background: saving ? 'var(--muted)' : 'var(--teal)', color: 'var(--bg)',
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {isMobile && onClose && (
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}
          >
            ✕
          </button>
        )}
      </div>

      <VersionTimeline versions={versions} currentStrategyId={currentStrategyId} onSelect={handleVersionSelect} />

      {saveNote && !runPrompt && (
        <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
          {saveNote}
        </div>
      )}

      {runPrompt && (
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          background: 'rgba(45,212,191,0.08)',
        }}>
          <span style={{ fontSize: 12, color: 'var(--text)', flex: 1, minWidth: 160 }}>
            Saved as "{runPrompt.name}". Run a backtest on it now?
          </span>
          <button
            onClick={handleRunNow}
            style={{
              padding: '5px 12px', borderRadius: 6, border: 'none', fontWeight: 600, fontSize: 12,
              background: 'var(--teal)', color: 'var(--bg)', cursor: 'pointer',
            }}
          >
            Run Backtest
          </button>
          <button
            onClick={() => setRunPrompt(null)}
            style={{
              padding: '5px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12,
              background: 'transparent', color: 'var(--muted)', border: '1px solid var(--border)', cursor: 'pointer',
            }}
          >
            Not now
          </button>
        </div>
      )}

      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {messages.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
            Describe a strategy in plain English to get started — e.g. "Buy when RSI crosses above
            30 and price is above the 50-day moving average." You can keep refining it, ask for a
            backtest, or ask why the results look a certain way — all in this conversation.
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} message={m} onSuggestionClick={send} onSaveClick={openSaveModal} onViewResults={onViewResults}
            onExportClick={(msg) => { track(Events.STRATEGY_EXPORT_OPENED, { source: 'builder_chat' }); setExportTarget({ graph: msg.graph, name: msg.name }); }} />
        ))}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 12 }}>
            <Spinner size={12} /> Thinking…
          </div>
        )}
      </div>

      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder={messages.length === 0
            ? 'Describe a strategy in plain English — the more detail (entry/exit rules, symbol, timing), the better…'
            : 'Message the AI builder…'}
          rows={1}
          style={{
            flex: 1,
            padding: '8px 10px',
            fontSize: 13,
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            resize: 'none',
            fontFamily: 'var(--font-body)',
            minHeight: messages.length === 0 ? 90 : 36,
            maxHeight: MAX_TEXTAREA_HEIGHT,
            overflowY: 'auto',
            boxSizing: 'border-box',
          }}
        />
        <button
          onClick={() => send(input)}
          disabled={sending || !input.trim()}
          style={{
            padding: '8px 14px',
            borderRadius: 6,
            border: 'none',
            fontWeight: 600,
            fontSize: 13,
            background: sending ? 'var(--muted)' : 'var(--teal)',
            color: 'var(--bg)',
            cursor: sending ? 'wait' : 'pointer',
          }}
        >
          Send
        </button>
      </div>

      {saveModal && (
        <SaveNameModal
          defaultName={saveModal.defaultName}
          saving={saving}
          onCancel={() => setSaveModal(null)}
          onConfirm={confirmSaveWithName}
        />
      )}

      {exportTarget && (
        <ExportStrategyModal
          graph={exportTarget.graph}
          name={exportTarget.name}
          onClose={() => setExportTarget(null)}
        />
      )}
    </div>
  );
}

function SaveNameModal({ defaultName, saving, onCancel, onConfirm }) {
  const [name, setName] = useState(defaultName || '');
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 24, maxWidth: 380, width: '100%',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Save Strategy</div>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onConfirm(name); }}
          placeholder="Strategy name…"
          style={{
            width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)',
            marginBottom: 16, boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => onConfirm(name)}
            disabled={saving || !name.trim()}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: saving || !name.trim() ? 'default' : 'pointer',
              background: 'var(--teal)', color: 'var(--bg)', border: 'none',
              opacity: saving || !name.trim() ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message, onSuggestionClick, onSaveClick, onViewResults, onExportClick }) {
  const isUser = message.role === 'user';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={{
        maxWidth: '90%',
        padding: '8px 12px',
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        background: isUser ? 'var(--teal)' : message.isError ? 'rgba(248,81,73,0.12)' : 'var(--surface)',
        color: isUser ? 'var(--bg)' : message.isError ? '#f85149' : 'var(--text)',
        border: isUser ? 'none' : '1px solid var(--border)',
      }}>
        {message.content}
      </div>
      {!isUser && message.fromCode && (
        <div style={{
          maxWidth: '90%', marginTop: 6, padding: '8px 10px', borderRadius: 8,
          background: 'rgba(242,182,50,0.12)', border: '1px solid rgba(242,182,50,0.4)',
          color: '#e3a008', fontSize: 12, lineHeight: 1.45,
        }}>
          ⚠️ Translated from pasted code — this is a close approximation, not an exact port.
          Review the entry/exit logic against your original before trusting the backtest.
        </div>
      )}
      {!isUser && message.diff && <div style={{ maxWidth: '95%', width: '100%' }}><GraphDiffCard diff={message.diff} /></div>}
      {!isUser && message.createdStrategy && onSaveClick && (
        <button
          onClick={onSaveClick}
          style={{
            marginTop: 6,
            padding: '5px 12px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            background: 'var(--teal)',
            color: 'var(--bg)',
            border: 'none',
          }}
        >
          💾 Save Strategy
        </button>
      )}
      {!isUser && message.createdStrategy && message.graph && onExportClick && (
        <button
          onClick={() => onExportClick(message)}
          style={{
            marginTop: 6, marginLeft: 6, padding: '5px 12px', borderRadius: 999,
            fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)',
          }}
        >
          ⬇ Export code
        </button>
      )}
      {!isUser && message.backtest && (
        <div style={{ maxWidth: '95%', width: '100%' }}>
          <BacktestSummaryCard backtest={message.backtest} onViewResults={onViewResults} />
        </div>
      )}
      {!isUser && message.suggestions && message.suggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, maxWidth: '95%' }}>
          {message.suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => onSuggestionClick(s)}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                background: 'var(--card)',
                color: 'var(--teal)',
                border: '1px solid var(--teal)',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
